/* eslint-disable react-hooks/exhaustive-deps */
import { QueryRequestHelper } from "@_common/query-request-helper";
import SearchableDropdownSelect from "@_common/SearchableDropdown";
import {
  getAuditStatusFilter,
  getVendorFilterLegislation,
} from "@services/audit.service";
import { getValue } from "@utils/lodash";
import moment from "moment";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface FilterOption {
  id: string;
  label: string;
  value: string;
}

interface FilterState {
  region_id: string;
  group_id: string;
  location_id: string;
  audit_status: string;
  vendor_id: string;
  state_id?: string;
  city_id?: string;
  legislation_id?: string;
  status?: string[];
}

interface AuditStatusFilterProps {
  date?: Date | string | null;
  onFilterChange?: () => void;
  TotalListCount?: any[];
  isAuditStatus?: boolean;
  MisType?: string;
}

const AuditStatusFilter: React.FC<AuditStatusFilterProps> = ({
  date,
  onFilterChange,
  TotalListCount,
  isAuditStatus,
  MisType,
}) => {
  const navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<FilterState>({
    region_id: params.region_id || "",
    group_id: params.group_id || "",
    location_id: params.location_id || "",
    audit_status: params.audit_status || "",
    vendor_id: params.vendor_id || "",
    state_id: params.state_id || "",
    city_id: params.city_id || "",
    legislation_id: params.legislation_id || "",
    status: params.status ? params.status.split(",") : [],
  });

  const [dropdownData, setDropdownData] = useState({
    vendors: [] as FilterOption[],
    regions: [] as FilterOption[],
    accounts: [] as FilterOption[],
    locations: [] as FilterOption[],
    statuses: [] as FilterOption[],
    state: [] as FilterOption[],
    city: [] as FilterOption[],
    legislations: [] as FilterOption[],
  });

  const dateParams = useMemo(() => {
    const targetDate = date || new Date();
    return {
      month: params.date
        ? params.date.split("-")[1]
        : moment(targetDate).format("MM"),
      year: params.date
        ? params.date.split("-")[0]
        : moment(targetDate).format("YYYY"),
    };
  }, [date, params.date]);

  const fetchFilterData = useCallback(
    async (
      type: string,
      dataKey: keyof typeof dropdownData,
      mapping: { id: string; label: string; value: string },
      view_Type?: string,
    ) => {
      try {
        const payload = { ...dateParams, type };
        const queryRequest = QueryRequestHelper(payload);
        const resp = view_Type
          ? await getVendorFilterLegislation(queryRequest)
          : await getAuditStatusFilter(queryRequest);
        if (resp.statusCode === 200) {
          const responseData =
            type === "CITY"
              ? getValue(resp, "data", [])
              : getValue(resp, "data", []);
          const mappedData = responseData.map((item: any) => ({
            ...item,
            id: getValue(item, mapping.id, ""),
            label: getValue(item, mapping.label, ""),
            value: getValue(item, mapping.value, ""),
          }));
          setDropdownData((prev) => ({ ...prev, [dataKey]: mappedData }));
        }
      } catch (error) {
        console.error(`Error fetching ${type}:`, error);
      }
    },
    [dateParams],
  );

  const loadAllFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchFilterData("VENDOR", "vendors", {
          id: "vendor_id",
          label: "vendor_name",
          value: "vendor_name",
        }),
        fetchFilterData("REGION", "regions", {
          id: "region_id",
          label: "region_name",
          value: "region_name",
        }),
        fetchFilterData("GROUP", "accounts", {
          id: "group_id",
          label: "group_name",
          value: "group_name",
        }),
        fetchFilterData("LOCATION", "locations", {
          id: "location_id",
          label: "location_name",
          value: "location_name",
        }),
        fetchFilterData("AUDIT_STATUS", "statuses", {
          id: "audit_status",
          label: "audit_status",
          value: "audit_status",
        }),
        fetchFilterData("STATE", "state", {
          id: "state_id",
          label: "state_name",
          value: "state_name",
        }),
        fetchFilterData("CITY", "city", {
          id: "city_id",
          label: "city_name",
          value: "city_name",
        }),
        fetchFilterData(
          "LEGISLATION",
          "legislations",
          {
            id: "id",
            label: "legislation_name",
            value: "legislation_name",
          },
          "LINE_ITEM_REPORT",
        ),
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchFilterData, dateParams]);

  useEffect(() => {
    if (!isInitialized) {
      loadAllFilters();
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialized && date) {
      loadAllFilters();
    }
  }, [date]);

  const updateURLAndNotifyParent = useCallback(
    (newParams: Record<string, string>) => {
      const searchParams = new URLSearchParams(window.location.search);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          searchParams.set(key, Array.isArray(value) ? value.join(",") : value);
        } else {
          searchParams.delete(key);
        }
      });
      navigate(`${window.location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [navigate],
  );

  const handleSelectFilter = useCallback(
    (id: any, name: keyof FilterState) => {
      const normalizedId =
        typeof id === "object" && id !== null ? getValue(id, "id", "") : id;

      setFilterState((prev: any) => {
        const newState = { ...prev, [name]: normalizedId };
        updateURLAndNotifyParent({ [name]: String(normalizedId || "") });
        return newState;
      });
    },
    [updateURLAndNotifyParent],
  );

  const handleRemoveFilter = useCallback(
    (name: keyof FilterState) => {
      setFilterState((prev) => {
        const newState = { ...prev, [name]: "" };
        updateURLAndNotifyParent({ [name]: "" });
        return newState;
      });
    },
    [updateURLAndNotifyParent],
  );

  const handleSubmitFilter = useCallback(() => {
    if (onFilterChange) {
      onFilterChange();
    }
  }, [onFilterChange]);

  const handleReset = useCallback(() => {
    const resetState = {
      region_id: "",
      group_id: "",
      location_id: "",
      audit_status: "",
      vendor_id: "",
      city_id: "",
      state_id: "",
      legislation_id: "",
      status: [],
    };
    setFilterState(resetState);
    const searchParams = new URLSearchParams(window.location.search);
    const preservedParams: Record<string, string> = {};
    if (searchParams.get("date")) {
      preservedParams.date = searchParams.get("date")!;
    }
    if (searchParams.get("search_text")) {
      preservedParams.search_text = searchParams.get("search_text")!;
    }
    if (searchParams.get("view_type")) {
      preservedParams.view_type = searchParams.get("view_type")!;
    }
    const newSearchParams = new URLSearchParams(preservedParams);
    navigate(`${window.location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  }, [navigate]);

  const getSelectedValue = useCallback(
    (options: FilterOption[], id: string) => {
      return options.find((item) => item.id === id) || "";
    },
    [],
  );

  const HIDE_STATUS_FILTER_FOR = [
    "SITE_MIS",
    "LOCATION",
    "REGION",
    "GROUP",
    "VENDOR",
    "COMPLIANCE_SCORECARD",
    "YEAR_HISTORY",
    "RECTIFICATION_REPORT",
    "LINE_ITEM_WISE_REPORT",
  ];

  const HIDE_LOCATION_FILTER_FOR = [
    "SITE_MIS",
    "LOCATION",
    "REGION",
    "GROUP",
    "VENDOR",
    "COMPLIANCE_SCORECARD",
    "LINE_ITEM_REPORT",
    "RECTIFICATION_REPORT",
    "YEAR_HISTORY",
    "LINE_ITEM_WISE_REPORT",
  ];

  const isInnerPage = window.location.pathname.includes(
    "/site_mis_detail/list",
  );
  if (
    isInnerPage &&
    (MisType === "LOCATION" ||
      MisType === "GROUP" ||
      MisType === "REGION" ||
      MisType === "VENDOR" ||
      MisType === "LINE_ITEM_WISE_REPORT" ||
      MisType === "YEAR_HISTORY" ||
      MisType === "RECTIFICATION_REPORT" ||
      MisType === "COMPLIANCE_SCORECARD")
  )
    return null;

  return (
    <div className="filter-section-wrap module-filter-wrapper">
      <div className="width-20">
        <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
          <label htmlFor="region_id" className="floating-label">
            Region
          </label>
          <SearchableDropdownSelect
            placeholder="Select Region"
            options={dropdownData.regions}
            handleChange={(val: any) =>
              val ? null : handleRemoveFilter("region_id")
            }
            handleClick={(e: any) =>
              handleSelectFilter(getValue(e, "id", ""), "region_id")
            }
            value={getSelectedValue(
              dropdownData.regions,
              filterState.region_id,
            )}
            label="label"
            id="id"
            name="region_id"
          />
        </div>
      </div>

      <div className="width-20">
        <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
          <label htmlFor="group_id" className="floating-label">
            Account
          </label>
          <SearchableDropdownSelect
            placeholder="Select Account"
            options={dropdownData.accounts}
            handleChange={(val: any) =>
              val ? null : handleRemoveFilter("group_id")
            }
            handleClick={(e: any) =>
              handleSelectFilter(getValue(e, "id", ""), "group_id")
            }
            value={getSelectedValue(
              dropdownData.accounts,
              filterState.group_id,
            )}
            label="label"
            id="id"
            name="group_id"
          />
        </div>
      </div>

      <div className="width-20">
        <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
          <label htmlFor="location_id" className="floating-label">
            Site
          </label>
          <SearchableDropdownSelect
            placeholder="Select Location"
            options={dropdownData.locations}
            handleChange={(val: any) =>
              val ? null : handleRemoveFilter("location_id")
            }
            handleClick={(e: any) =>
              handleSelectFilter(getValue(e, "id", ""), "location_id")
            }
            value={getSelectedValue(
              dropdownData.locations,
              filterState.location_id,
            )}
            label="label"
            id="id"
            name="location_id"
          />
        </div>
      </div>



      <div className="width-20">
        <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
          <label htmlFor="vendor_id" className="floating-label">
            Vendor
          </label>
          <SearchableDropdownSelect
            placeholder="Select Vendor"
            options={dropdownData.vendors}
            handleChange={(val: any) =>
              val ? null : handleRemoveFilter("vendor_id")
            }
            handleClick={(e: any) =>
              handleSelectFilter(getValue(e, "id", ""), "vendor_id")
            }
            value={getSelectedValue(
              dropdownData.vendors,
              filterState.vendor_id,
            )}
            label="label"
            id="id"
            name="vendor_id"
          />
        </div>
      </div>

      {!isAuditStatus && !HIDE_LOCATION_FILTER_FOR.includes(MisType ?? "") && (
        <>
          <div className="width-20">
            <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
              <label htmlFor="state_id" className="floating-label">
                State
              </label>
              <SearchableDropdownSelect
                placeholder="Select State"
                options={dropdownData.state}
                handleChange={(val: any) =>
                  val ? null : handleRemoveFilter("state_id")
                }
                handleClick={(e: any) =>
                  handleSelectFilter(getValue(e, "id", ""), "state_id")
                }
                value={getSelectedValue(
                  dropdownData.state,
                  filterState.state_id ?? "",
                )}
                label="label"
                id="id"
                name="state_id"
              />
            </div>
          </div>


        </>
      )}
      <div className="width-20">
        <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
          <label htmlFor="state_id" className="floating-label">
            City
          </label>
          <SearchableDropdownSelect
            placeholder="Select City"
            options={dropdownData.city}
            handleChange={(val: any) =>
              val ? null : handleRemoveFilter("city_id")
            }
            handleClick={(e: any) =>
              handleSelectFilter(getValue(e, "id", ""), "city_id")
            }
            value={getSelectedValue(
              dropdownData.city,
              filterState.city_id ?? "",
            )}
            label="label"
            id="id"
            name="city_id"
          />
        </div>
      </div>
      {(isAuditStatus || MisType === "LINE_ITEM_REPORT") &&
        !HIDE_STATUS_FILTER_FOR.includes(MisType ?? "") && (
          <div className="width-20">
            <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
              <label htmlFor="audit_status" className="floating-label">
                All Status
              </label>
              <SearchableDropdownSelect
                placeholder="Select All Status"
                options={dropdownData.statuses}
                handleChange={(val: any) =>
                  val ? null : handleRemoveFilter("audit_status")
                }
                handleClick={(e: any) =>
                  handleSelectFilter(getValue(e, "id", ""), "audit_status")
                }
                value={getSelectedValue(
                  dropdownData.statuses,
                  filterState.audit_status,
                )}
                label="label"
                id="id"
                name="audit_status"
              />
            </div>
          </div>
        )}

      {MisType === "LINE_ITEM_REPORT" && (
        <div className="width-20">
          <div className="filter-form-wrappper__location-input module_filter_fields_wrapper">
            <label htmlFor="legislation_id" className="floating-label">
              Check List
            </label>
            <SearchableDropdownSelect
              placeholder="Select CheckList"
              options={dropdownData.legislations}
              handleChange={(val: any) =>
                val ? null : handleRemoveFilter("legislation_id")
              }
              handleClick={(e: any) =>
                handleSelectFilter(getValue(e, "id", ""), "legislation_id")
              }
              value={getSelectedValue(
                dropdownData.legislations,
                filterState.legislation_id || "",
              )}
              label="label"
              id="id"
              name="legislation_id"
            />
          </div>
        </div>
      )}



      <div className="width-18 gap-4">
        <div className="filter-form-wrappper__cta-wrapper">
          <button
            className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--search btn btn-primary"
            onClick={handleSubmitFilter}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Search"}
          </button>
          <button
            className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--reset btn "
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </button>
          {/* <span className="mx-2"> Total Count: {TotalListCount?.length}</span> */}
        </div>
      </div>
    </div>
  );
};

export default AuditStatusFilter;
